import { t, ui, uiError } from '../i18n';
import { getWebappApiUrl, webappFetch } from '../utils/apiConfig';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  AccountTree as IntegrationIcon,
  Assessment as TodayIcon,
  ExpandMore as ExpandMoreIcon,
  Public as PublicIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

interface UserInfo {
  username: string;
  displayName: string;
  role: string;
  orgName: string;
  orgIcon: string;
  loginTime: string;
}

type Partner = 'eINS' | 'KAIST' | 'KULS';
function formatBytes(bytes?: number) {
  if (typeof bytes !== 'number') return t('미측정');
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function formatTime(value: string) {
  return new Date(value).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatRelativeTimeKo(value?: string | null) {
  if (!value) return t("기록 없음");
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return t("기록 없음");

  const diffMinutes = Math.max(0, Math.floor((Date.now() - timestamp) / (1000 * 60)));
  if (diffMinutes < 1) return t("방금 전");
  if (diffMinutes < 60) return t("{0}분 전", [diffMinutes]);

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return t("{0}시간 전", [diffHours]);

  const diffDays = Math.floor(diffHours / 24);
  return t("{0}일 전", [diffDays]);
}

function koreaDateKey(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

function shortHash(value?: string) {
  if (!value) return '-';
  if (value.length <= 24) return value;
  return `${value.slice(0, 18)}...${value.slice(-8)}`;
}

function tinyHash(value?: string) {
  if (!value) return '-';
  if (value.length <= 18) return value;
  return `${value.slice(0, 8)}...${value.slice(-6)}`;
}

function shortBusinessId(value?: string) {
  if (!value) return '-';
  if (value.length <= 34) return value;
  return `${value.slice(0, 24)}...${value.slice(-8)}`;
}

function blockNumberValue(value?: string | null) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function DetailField({
  label,
  value,
  mono = false
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: '#fff' }}>
      <Typography variant="caption" color="text.secondary" fontWeight="bold">
        {ui(label)}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        sx={{
          mt: 0.5,
          fontFamily: mono ? 'monospace' : 'inherit',
          fontWeight: 600,
          wordBreak: 'break-word'
        }}
      >
        {value === undefined || value === null || value === '' ? '-' : value}
      </Typography>
    </Paper>
  );
}

function DashboardMetricCard({
  label,
  value,
  caption,
  color,
  icon,
  gradient,
  highlight = false
}: {
  label: string;
  value: React.ReactNode;
  caption: React.ReactNode;
  color: string;
  icon?: React.ReactNode;
  gradient?: string;
  highlight?: boolean;
}) {
  return (
    <Card
      sx={{
        height: '100%',
        color: gradient ? '#fff' : 'inherit',
        background: gradient || '#fff',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        },
        ...(highlight ? {
          animation: 'prototypeMetricPulse 4s ease-out',
          '@keyframes prototypeMetricPulse': {
            '0%': { boxShadow: `0 0 0 0 ${color}66`, transform: 'translateY(-2px)' },
            '45%': { boxShadow: `0 0 0 7px ${color}22` },
            '100%': { boxShadow: 'none', transform: 'translateY(0)' }
          }
        } : {})
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, lineHeight: 1.05, wordBreak: 'break-word' }}>
              {value}
            </Typography>
            <Typography variant="body2" sx={{ opacity: gradient ? 0.92 : 0.78, fontWeight: 700 }}>
              {ui(label)}
            </Typography>
            <Typography component="div" variant="caption" sx={{ opacity: gradient ? 0.82 : 0.68, fontSize: '0.75rem', mt: 0.25 }}>
              {caption}
            </Typography>
          </Box>
          {icon && (
            <Box sx={{ opacity: 0.8, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function RawJsonAccordion({
  title,
  data
}: {
  title: string;
  data: unknown;
}) {
  const [expanded, setExpanded] = useState(false);
  const [renderedJson, setRenderedJson] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    setRenderedJson(null);
    setRendering(false);
  }, [data]);

  useEffect(() => {
    if (!expanded || renderedJson !== null) return;

    setRendering(true);
    let cancelled = false;
    let firstFrame = 0;
    let secondFrame = 0;
    let timeoutId = 0;

    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        timeoutId = window.setTimeout(() => {
          if (cancelled) return;
          try {
            const nextJson = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
            setRenderedJson(nextJson);
          } catch (error) {
            setRenderedJson(`원문 payload를 문자열로 변환하지 못했습니다: ${error instanceof Error ? error.message : 'unknown error'}`);
          } finally {
            setRendering(false);
          }
        }, 0);
      });
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      window.clearTimeout(timeoutId);
    };
  }, [expanded, renderedJson, data]);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
      sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack spacing={0.25}>
          <Typography variant="subtitle2" fontWeight="bold">{ui(title)}</Typography>
          <Typography variant="caption" color="text.secondary">{t("대용량 payload는 펼치는 순간 원문 표시를 준비합니다.")}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', maxHeight: 360, overflow: 'auto' }}>
          {rendering || renderedJson === null ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">{t("원문 payload 표시를 준비하는 중입니다. eINS처럼 rows가 많은 payload는 잠시 걸릴 수 있습니다.")}</Typography>
            </Stack>
          ) : (
            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{renderedJson}</pre>
          )}
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
}

interface LivePrototypeTransaction {
  transaction_id: string;
  block_number: string;
  transaction_index: number;
  sender: string | null;
  receiver: string | null;
  item: string | null;
  timestamp: string | null;
  status: string | null;
  creator_msp_id: string | null;
  chaincode_name: string | null;
  chaincode_version: string | null;
  record_type: string | null;
  business_id_type: string | null;
  business_id: string | null;
  operation_type: string | null;
  payload_hash: string | null;
  payload_size_bytes: number | null;
  payload_storage_type: string | null;
  payload_ref: string | null;
  block_hash: string | null;
  previous_hash: string | null;
  data_hash: string | null;
  transaction_count: number | string | null;
  block_created_at: string;
  function_name: string | null;
  arg1: string | null;
  arg2: string | null;
  arg3: string | null;
  arg4: string | null;
  arg5: string | null;
  arg6: string | null;
  arg7: string | null;
  arg8: string | null;
  arg9: string | null;
  arg10: string | null;
}

interface PayloadProof {
  hashMatches?: boolean;
  evidenceMatches?: boolean;
  verificationStatus?: 'VERIFIED' | 'MISMATCH' | 'PAYLOAD_UNAVAILABLE' | 'UNAVAILABLE';
  verificationSource?: string;
  topLevelKeys?: string[];
  rawPayload?: Record<string, any>;
  status?: string;
  blockNumber?: string;
}

interface PrototypeStreamSnapshot {
  fromBlock: number;
  limit: number;
  count: number;
  earliestBlock: number | null;
  latestBlock: number | null;
  signature: string;
  emittedAt: string;
  source: string;
  message?: string;
  transactions?: LivePrototypeTransaction[];
}

interface PrototypeBlockGroup {
  blockNumber: string;
  partnerRows: LivePrototypeTransaction[];
  allRows: LivePrototypeTransaction[];
  representative: LivePrototypeTransaction;
}

interface NetworkStats {
  latestBlockNumber: number;
  totalTransactions: number;
}

const UPGRADE_COMMIT_BLOCK = 324;
const PROTOTYPE_FROM_BLOCK = UPGRADE_COMMIT_BLOCK + 1;
const KAIST_OPERATION_ORDER = ['assignment', 'retrieval', 'relocation'];
const EINS_SCEN_TYPE_ORDER = ['Daily_Opt', 'Emergency_Opt'];
const CONFIRMED_OPERATION_OPTIONS = [...EINS_SCEN_TYPE_ORDER, ...KAIST_OPERATION_ORDER];

function normalizeOrg(value?: string | null) {
  if (!value) return '';
  const upper = value.toUpperCase();
  if (upper === 'EINS' || upper === 'EINSMSP') return 'EINS';
  if (upper === 'KAIST' || upper === 'KAISTMSP') return 'KAIST';
  if (upper === 'KULS' || upper === 'KULSMSP') return 'KULS';
  return value;
}

function sourceOrgOf(tx: LivePrototypeTransaction) {
  return normalizeOrg(tx.sender || tx.creator_msp_id);
}

function targetOrgOf(tx: LivePrototypeTransaction) {
  return normalizeOrg(tx.receiver);
}

function isEinsTx(tx: LivePrototypeTransaction) {
  return sourceOrgOf(tx) === 'EINS';
}

function isKaistTx(tx: LivePrototypeTransaction) {
  return sourceOrgOf(tx) === 'KAIST';
}

function isKulsRelatedTx(tx: LivePrototypeTransaction) {
  return sourceOrgOf(tx) === 'KULS' || targetOrgOf(tx) === 'KULS';
}

function sortLiveTransactions(rows: LivePrototypeTransaction[]) {
  return rows.slice().sort((a, b) => {
    const blockDiff = blockNumberValue(b.block_number) - blockNumberValue(a.block_number);
    if (blockDiff !== 0) return blockDiff;
    return (b.transaction_index || 0) - (a.transaction_index || 0);
  });
}

function classifyLiveTx(tx: LivePrototypeTransaction) {
  if (tx.chaincode_name === '_lifecycle') return 'lifecycle';
  if (tx.function_name === 'RecordIntegrationResult') return 'recordIntegrationResult';
  if (tx.function_name === 'RecordEvent') return 'legacyRecordEvent';
  return 'other';
}

function categoryLabel(tx: LivePrototypeTransaction) {
  const category = classifyLiveTx(tx);
  if (category === 'lifecycle') return t("체인코드 업그레이드 / Chaincode lifecycle");
  if (category === 'recordIntegrationResult') return t("신규 v1 기록 / New v1 record");
  if (category === 'legacyRecordEvent') return t("기존 명세 호환 / Legacy compatibility");
  return t("기타 / Other");
}

function categoryColor(tx: LivePrototypeTransaction): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  const category = classifyLiveTx(tx);
  if (category === 'lifecycle') return 'secondary';
  if (category === 'recordIntegrationResult') return 'success';
  if (category === 'legacyRecordEvent') return 'info';
  return 'default';
}

function safeParsePayloadJSON(value: string | null): Record<string, any> | null {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function legacyPayload(tx: LivePrototypeTransaction) {
  return tx.function_name === 'RecordEvent' ? safeParsePayloadJSON(tx.arg10) : null;
}

function liveOperation(tx: LivePrototypeTransaction) {
  if (tx.function_name === 'RecordIntegrationResult') return tx.operation_type || '-';
  if (tx.function_name === 'RecordEvent') return tx.arg9 || tx.operation_type || tx.item || '-';
  if (tx.function_name === 'ApproveChaincodeDefinitionForMyOrg') return 'approve';
  if (tx.function_name === 'CommitChaincodeDefinition') return 'commit';
  return tx.operation_type || tx.item || '-';
}

function liveBusinessId(tx: LivePrototypeTransaction) {
  if (tx.function_name === 'RecordIntegrationResult') {
    return tx.business_id_type && tx.business_id ? `${tx.business_id_type}: ${tx.business_id}` : '-';
  }
  if (tx.function_name === 'RecordEvent') {
    const payload = legacyPayload(tx);
    if (payload?.request_id) return `request_id: ${payload.request_id}`;
    return tx.arg1 ? `recordId: ${tx.arg1}` : '-';
  }
  return '-';
}

function livePayloadSizeBytes(tx: LivePrototypeTransaction) {
  if (typeof tx.payload_size_bytes === 'number') return tx.payload_size_bytes;
  if (tx.function_name === 'RecordEvent' && tx.arg10) {
    return new TextEncoder().encode(tx.arg10).length;
  }
  return undefined;
}

function liveEvidenceNote(tx: LivePrototypeTransaction, proof?: PayloadProof) {
  if (tx.function_name === 'RecordIntegrationResult') {
    if (proof?.verificationStatus === 'VERIFIED') {
      return 'peer 원장 증거 일치 / Peer-ledger evidence matched';
    }
    if (proof?.verificationStatus === 'MISMATCH') {
      return '증거 불일치 / Evidence mismatch';
    }
    return '원장 증거 확인 대상 / Peer-ledger check target';
  }
  if (tx.function_name === 'RecordEvent') {
    return 'payloadHash/ref 없음 / No payloadHash or payloadRef in legacy RecordEvent';
  }
  if (tx.chaincode_name === '_lifecycle') {
    return '업그레이드 절차 증거 / Lifecycle evidence';
  }
  return '-';
}

function liveRecordType(tx: LivePrototypeTransaction) {
  if (tx.function_name === 'RecordIntegrationResult') return tx.record_type || '신규 v1 기록';
  if (tx.function_name === 'RecordEvent') return '기존 RecordEvent 호환';
  return tx.function_name || '-';
}

function ledgerStatusLabel(tx: LivePrototypeTransaction) {
  if (tx.chaincode_name === '_lifecycle') return t("원장 반영 / LIFECYCLE COMMITTED");
  if (tx.block_number && tx.transaction_id) return t("기록 완료 / COMMITTED");
  return t("원장 확인 중 / PENDING");
}

function indexStatusLabel(tx: LivePrototypeTransaction) {
  if (tx.block_number && tx.transaction_id) return t("WebApp 반영 완료 / INDEXED");
  return t("색인 확인 중 / INDEXING");
}

function integrityStatusLabel(tx: LivePrototypeTransaction, proof?: PayloadProof) {
  const category = classifyLiveTx(tx);
  if (category === 'recordIntegrationResult') {
    if (proof?.verificationStatus === 'VERIFIED') return t("검증 완료 / VERIFIED");
    if (proof?.verificationStatus === 'MISMATCH') return t("증거 불일치 / MISMATCH");
    if (proof?.verificationStatus === 'PAYLOAD_UNAVAILABLE' || proof?.verificationStatus === 'UNAVAILABLE') {
      return t("검증 불가 / UNAVAILABLE");
    }
    return t("검증 대기 / CHECKING");
  }
  if (category === 'legacyRecordEvent') return t("hash-ref 없음 / LEGACY");
  if (category === 'lifecycle') return t("업무 검증 아님 / NOT APPLICABLE");
  return t("검증 대상 아님 / NOT APPLICABLE");
}

function recordStatusLabel(tx: LivePrototypeTransaction) {
  const status = tx.status?.trim();
  if (!status) return t("업무 상태 미제공");
  if (status.toUpperCase() === 'SUBMITTED') return t("업무 제출됨 / SUBMITTED");
  return t("업무 상태: {0}", [status]);
}

function liveStatus(tx: LivePrototypeTransaction) {
  return ledgerStatusLabel(tx);
}

function LedgerStatusChip({ tx }: { tx: LivePrototypeTransaction }) {
  const committed = Boolean(tx.block_number && tx.transaction_id);
  return (
    <Chip
      size="small"
      color={committed ? 'success' : 'warning'}
      variant={committed ? 'filled' : 'outlined'}
      label={committed ? t("COMMITTED") : t("PENDING")}
      title={ui(ledgerStatusLabel(tx))}
    />
  );
}

function IndexStatusChip({ tx }: { tx: LivePrototypeTransaction }) {
  const indexed = Boolean(tx.block_number && tx.transaction_id);
  return (
    <Chip
      size="small"
      color={indexed ? 'info' : 'warning'}
      variant="outlined"
      label={indexed ? t("INDEXED") : t("INDEXING")}
      title={ui(indexStatusLabel(tx))}
    />
  );
}

function IntegrityStatusChip({
  tx,
  proof
}: {
  tx: LivePrototypeTransaction;
  proof?: PayloadProof;
}) {
  const category = classifyLiveTx(tx);

  if (category !== 'recordIntegrationResult') {
    return (
      <Chip
        size="small"
        variant="outlined"
        color={category === 'legacyRecordEvent' ? 'default' : 'secondary'}
        label={category === 'legacyRecordEvent' ? t("hash-ref 없음") : t("업무 검증 아님")}
        sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
      />
    );
  }

  const matched = proof?.verificationStatus === 'VERIFIED';
  const mismatched = proof?.verificationStatus === 'MISMATCH';
  return (
    <Chip
      size="small"
      variant={matched ? 'filled' : 'outlined'}
      color={matched ? 'success' : mismatched ? 'error' : 'warning'}
      label={matched ? t("VERIFIED") : mismatched ? t("MISMATCH") : t("CHECKING")}
      title={ui(integrityStatusLabel(tx, proof))}
    />
  );
}

function getRowsCount(payload: Record<string, any> | null | undefined, key: string) {
  const target = payload?.[key];
  if (!target) return undefined;
  if (typeof target.row_count === 'number') return target.row_count;
  if (Array.isArray(target.rows)) return target.rows.length;
  return undefined;
}

function getMovesCount(payload: Record<string, any> | null | undefined) {
  if (!payload) return undefined;
  if (Array.isArray(payload.moves)) return payload.moves.length;
  if (typeof payload.summary?.move_count === 'number') return payload.summary.move_count;
  return undefined;
}

function LiveTxDetailDialog({
  tx,
  proof,
  onClose
}: {
  tx: LivePrototypeTransaction | null;
  proof?: PayloadProof;
  onClose: () => void;
}) {
  const payload = tx?.function_name === 'RecordEvent' ? safeParsePayloadJSON(tx.arg10) : null;
  const legacyMoves = payload?.moves;

  return (
    <Dialog open={Boolean(tx)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t("트랜잭션 상세 / Transaction Detail")}</DialogTitle>
      <DialogContent dividers>
        {tx && (
          <Stack spacing={2}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("분류 / Category")} value={categoryLabel(tx)} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("블록 / Block")} value={`#${tx.block_number}`} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("함수 / Function")} value={tx.function_name || '-'} mono /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Creator MSP")} value={tx.creator_msp_id || '-'} mono /></Grid>
              <Grid item xs={12} sm={6}><DetailField label={t("Tx ID")} value={tx.transaction_id} mono /></Grid>
              <Grid item xs={12} sm={6}><DetailField label={t("Block Hash")} value={tx.block_hash || '-'} mono /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("발신 / Sender")} value={tx.sender || '-'} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("수신 / Receiver")} value={tx.receiver || '-'} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("작업 / Operation")} value={liveOperation(tx)} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("체인코드 / Chaincode")} value={`${tx.chaincode_name || '-'} ${tx.chaincode_version || ''}`} mono /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("원장 상태 / Ledger")} value={<LedgerStatusChip tx={tx} />} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("색인 상태 / WebApp Index")} value={<IndexStatusChip tx={tx} />} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("무결성 / Integrity")} value={<IntegrityStatusChip tx={tx} proof={proof} />} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("업무 상태 / Record Status")} value={recordStatusLabel(tx)} /></Grid>
            </Grid>

            {tx.function_name === 'RecordIntegrationResult' && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{t("신규 v1 증거 / New v1 Evidence")}</Typography>
                <Alert severity={proof?.verificationStatus === 'VERIFIED' ? 'success' : proof?.verificationStatus === 'MISMATCH' ? 'error' : 'warning'} sx={{ mb: 2 }}>{t("hash-ref on-chain 구조입니다. 무결성 판정은 peer 원장의 payloadHash와 payloadRef를 기준으로 PostgreSQL 원문과 WebApp 색인 증거를 교차 확인한 결과입니다.")}</Alert>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("Record Type")} value={tx.record_type || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("Business ID")} value={liveBusinessId(tx)} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("Payload Size")} value={formatBytes(tx.payload_size_bytes || undefined)} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("Hash 검증 / Hash Check")} value={integrityStatusLabel(tx, proof)} /></Grid>
                  <Grid item xs={12} sm={6}><DetailField label={t("원문 저장 방식 / Storage")} value={tx.payload_storage_type || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6}><DetailField label={t("검증 방식 / Verification")} value={t("canonical JSON SHA-256 재계산 후 on-chain hash 비교")} /></Grid>
                  <Grid item xs={12}><DetailField label={t("Payload Hash")} value={tx.payload_hash || '-'} mono /></Grid>
                  <Grid item xs={12}><DetailField label={t("Payload Ref")} value={tx.payload_ref || '-'} mono /></Grid>
                  <Grid item xs={12}><DetailField label={t("Raw Payload Keys")} value={proof?.topLevelKeys?.join(', ') || '-'} mono /></Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  {proof?.rawPayload ? <RawJsonAccordion title={t("원문 payload / Raw Payload")} data={proof.rawPayload} /> : <Typography color="text.secondary">{uiError(proof?.status || '원문 조회 전 또는 조회 실패')}</Typography>}
                </Box>
              </Paper>
            )}

            {tx.function_name === 'RecordEvent' && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{t("기존 RecordEvent 인자 / Legacy RecordEvent Arguments")}</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("recordId / args[0]")} value={tx.arg1 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("pipelineId / args[1]")} value={tx.arg2 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("stage / args[2]")} value={tx.arg3 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("prevTxId / args[3]")} value={tx.arg4 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("sender / args[4]")} value={tx.arg5 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("receiver / args[5]")} value={tx.arg6 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("containerRef / args[6]")} value={tx.arg7 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("scenarioRef / args[7]")} value={tx.arg8 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("operationType / args[8]")} value={tx.arg9 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("moves[] count")} value={Array.isArray(legacyMoves) ? legacyMoves.length : '-'} /></Grid>
                </Grid>
                <RawJsonAccordion title={t("payloadJSON / args[9]")} data={payload || tx.arg10 || {}} />
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("닫기 / Close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

function BlockDetailDialog({
  block,
  partnerLabel,
  proofs,
  isPartnerTransaction,
  onClose,
  onSelectTx
}: {
  block: PrototypeBlockGroup | null;
  partnerLabel: string;
  proofs: Record<string, PayloadProof>;
  isPartnerTransaction: (tx: LivePrototypeTransaction) => boolean;
  onClose: () => void;
  onSelectTx: (tx: LivePrototypeTransaction) => void;
}) {
  const representative = block?.representative || null;
  const sortedAllRows = (block?.allRows || []).slice().sort((a, b) => (a.transaction_index || 0) - (b.transaction_index || 0));
  const partnerTxCount = sortedAllRows.filter(isPartnerTransaction).length;
  const totalTxCount = representative?.transaction_count || sortedAllRows.length;

  return (
    <Dialog open={Boolean(block)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t("블록 상세 / Block Detail")}</DialogTitle>
      <DialogContent dividers>
        {block && representative && (
          <Stack spacing={2}>
            <Alert severity="info">{t("이 화면은 같은 블록에 포함된 트랜잭션 목록을 함께 보여줍니다.")}{" "}{partnerLabel}{" "}{t("계정에서는")}{" "}{partnerLabel}{" "}{t("관련 트랜잭션만 원문 payload 상세와 hash-ref 검증 근거를 열람할 수 있고, 타 기관 데이터는 블록 포함 여부와 기본 메타데이터만 표시합니다.")}</Alert>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("블록 / Block")} value={`#${block.blockNumber}`} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("생성시각")} value={formatTime(representative.block_created_at)} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("블록 내 전체 Tx")} value={t("{0}건", [totalTxCount])} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("{0} 관련 Tx", [partnerLabel])} value={t("{0}건", [partnerTxCount])} /></Grid>
              <Grid item xs={12} sm={6}><DetailField label={t("Block Hash")} value={representative.block_hash || '-'} mono /></Grid>
              <Grid item xs={12} sm={6}><DetailField label={t("Data Hash")} value={representative.data_hash || '-'} mono /></Grid>
              <Grid item xs={12}><DetailField label={t("Previous Hash")} value={representative.previous_hash || '-'} mono /></Grid>
            </Grid>

            <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ width: 42 }}><strong>#</strong></TableCell>
                    <TableCell sx={{ width: 82 }}><strong>{t("기관")}</strong></TableCell>
                    <TableCell sx={{ width: 135 }}><strong>{t("기록 유형")}</strong></TableCell>
                    <TableCell sx={{ width: 200 }}><strong>{t("업무 ID")}</strong></TableCell>
                    <TableCell sx={{ width: 100 }}><strong>{t("작업")}</strong></TableCell>
                    <TableCell sx={{ width: 112 }}><strong>{t("Tx ID")}</strong></TableCell>
                    <TableCell sx={{ width: 245 }}><strong>{t("열람")}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedAllRows.map((tx) => {
                    const ownTx = isPartnerTransaction(tx);
                    return (
                      <TableRow
                        hover={ownTx}
                        key={tx.transaction_id}
                        sx={{ cursor: ownTx ? 'pointer' : 'default' }}
                        onClick={() => {
                          if (ownTx) {
                            onSelectTx(tx);
                          }
                        }}
                      >
                        <TableCell>{tx.transaction_index}</TableCell>
                        <TableCell>{tx.sender || tx.creator_msp_id || '-'}</TableCell>
                        <TableCell>
                          <Chip size="small" color={categoryColor(tx)} label={liveRecordType(tx)} />
                        </TableCell>
                        <TableCell
                          title={liveBusinessId(tx)}
                          sx={{
                            fontFamily: 'monospace',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0
                          }}
                        >
                          {shortBusinessId(liveBusinessId(tx))}
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{liveOperation(tx)}</TableCell>
                        <TableCell
                          title={tx.transaction_id}
                          sx={{
                            fontFamily: 'monospace',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {tinyHash(tx.transaction_id)}
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden' }}>
                          {ownTx ? (
                            <Stack spacing={0.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                              <Chip size="small" color="primary" variant="outlined" label={t("상세 열람 가능")} sx={{ maxWidth: '100%' }} />
                              <LedgerStatusChip tx={tx} />
                            </Stack>
                          ) : (
                            <Stack spacing={0.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t("타 기관 데이터")}
                                title={t("타 기관 데이터입니다.")}
                                sx={{ maxWidth: '100%' }}
                              />
                              <Chip
                                size="small"
                                color="default"
                                variant="outlined"
                                label={t("상세 열람 제한")}
                                title={t("현재 계정에서는 타 기관 원문 payload 상세를 열람할 수 없습니다.")}
                                sx={{ maxWidth: '100%' }}
                              />
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("닫기 / Close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

function PartnerIntegrationPrototype({ partner, user }: { partner: Partner; user: UserInfo }) {
  const [tab, setTab] = useState(0);
  const [transactions, setTransactions] = useState<LivePrototypeTransaction[]>([]);
  const [proofs, setProofs] = useState<Record<string, PayloadProof>>({});
  const [selectedTx, setSelectedTx] = useState<LivePrototypeTransaction | null>(null);
  const [selectedBlockNumber, setSelectedBlockNumber] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [kulsOrgFilter, setKulsOrgFilter] = useState('ALL');
  const [kulsOperationFilter, setKulsOperationFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [networkStats, setNetworkStats] = useState<NetworkStats | null>(null);
  const [newTransactionIds, setNewTransactionIds] = useState<Set<string>>(new Set());
  const [newBlockNumbers, setNewBlockNumbers] = useState<Set<string>>(new Set());
  const sseWatchMs = 2000;
  const fullRefreshMs = 30000;
  const isLoadingRef = useRef(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const knownTxIdsRef = useRef<Set<string>>(new Set());
  const knownBlockNumbersRef = useRef<Set<string>>(new Set());
  const initializedHighlightsRef = useRef(false);
  const highlightTimersRef = useRef<number[]>([]);

  const isKaist = partner === 'KAIST';
  const isKuls = partner === 'KULS';
  const partnerLabel = isKaist ? 'KAIST' : isKuls ? 'KULS' : 'eINS';
  const partnerRecordScopeLabel = isKuls ? 'KULS 관련' : partnerLabel;
  const sourceOrg = isKaist ? 'KAIST' : isKuls ? 'KULS' : 'EINS';
  const endpointCandidate = isKaist
    ? '/backend/api/v1/integration/kaist/yard-optimization-results'
    : isKuls
      ? 'KULS 직접 invoke endpoint는 현재 확정하지 않습니다.'
      : '/backend/api/v1/integration/eins/dispatch-optimization-results';

  const isPartnerTransaction = useCallback((tx: LivePrototypeTransaction) => {
    if (partner === 'KAIST') return isKaistTx(tx);
    if (partner === 'KULS') return isKulsRelatedTx(tx);
    return isEinsTx(tx);
  }, [partner]);

  const hydrateTransactions = useCallback(async (
    inputRows: LivePrototypeTransaction[],
    options: { syncedAt?: string; animateNew?: boolean } = {}
  ) => {
    const rows = sortLiveTransactions(inputRows);
    const nextTxIds = new Set(rows.map((tx) => tx.transaction_id).filter(Boolean) as string[]);
    const nextBlockNumbers = new Set(rows.map((tx) => tx.block_number).filter(Boolean) as string[]);

    if (initializedHighlightsRef.current && options.animateNew !== false) {
      const newTxIds = Array.from(nextTxIds).filter((id) => !knownTxIdsRef.current.has(id));
      const newBlocks = Array.from(nextBlockNumbers).filter((blockNumber) => !knownBlockNumbersRef.current.has(blockNumber));

      if (newTxIds.length || newBlocks.length) {
        setNewTransactionIds((previous) => {
          const merged = new Set(previous);
          newTxIds.forEach((id) => merged.add(id));
          return merged;
        });
        setNewBlockNumbers((previous) => {
          const merged = new Set(previous);
          newBlocks.forEach((blockNumber) => merged.add(blockNumber));
          return merged;
        });

        const timer = window.setTimeout(() => {
          setNewTransactionIds((previous) => {
            const next = new Set(previous);
            newTxIds.forEach((id) => next.delete(id));
            return next;
          });
          setNewBlockNumbers((previous) => {
            const next = new Set(previous);
            newBlocks.forEach((blockNumber) => next.delete(blockNumber));
            return next;
          });
        }, 4000);
        highlightTimersRef.current.push(timer);
      }
    }

    knownTxIdsRef.current = nextTxIds;
    knownBlockNumbersRef.current = nextBlockNumbers;
    initializedHighlightsRef.current = true;
    setTransactions(rows);

    // Raw partner payloads are loaded only when an authorized user opens detail.
    setProofs({});
  }, []);

  const loadData = useCallback(async ({ initial = false }: { initial?: boolean } = {}) => {
    if (isLoadingRef.current) return;
    try {
      isLoadingRef.current = true;
      if (initial) {
        setLoading(true);
      }
      setError(null);

      const statsRequest = webappFetch(`${getWebappApiUrl()}/api/blockchain/statistics`)
        .then((statsResponse) => statsResponse.json())
        .catch(() => null);
      const response = await webappFetch(`${getWebappApiUrl()}/api/blockchain/integration-prototype/transactions?fromBlock=${PROTOTYPE_FROM_BLOCK}&limit=500`);
      const body = await response.json();
      if (!response.ok || !body.success) {
        throw new Error(body.message || body.error || `HTTP ${response.status}`);
      }

      const statsBody = await statsRequest;
      if (statsBody?.success && statsBody.data) {
        setNetworkStats({
          latestBlockNumber: Number(statsBody.data.latestBlockNumber || 0),
          totalTransactions: Number(statsBody.data.totalTransactions || 0)
        });
      }

      await hydrateTransactions(body.data.transactions as LivePrototypeTransaction[], { animateNew: !initial });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      if (initial) {
        setLoading(false);
      }
      isLoadingRef.current = false;
    }
  }, [hydrateTransactions]);

  useEffect(() => {
    loadData({ initial: true });
  }, [loadData]);

  useEffect(() => () => {
    highlightTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    highlightTimersRef.current = [];
  }, []);

  useEffect(() => {
    setError(null);

    const source = new EventSource(
      `${getWebappApiUrl()}/api/blockchain/integration-prototype/events?fromBlock=${PROTOTYPE_FROM_BLOCK}&limit=500&intervalMs=${sseWatchMs}`,
      { withCredentials: true }
    );
    eventSourceRef.current = source;
    let closed = false;

    const readSnapshot = (event: Event): PrototypeStreamSnapshot | null => {
      try {
        return JSON.parse((event as MessageEvent<string>).data) as PrototypeStreamSnapshot;
      } catch {
        return null;
      }
    };

    const handleConnected = (event: Event) => {
      const body = readSnapshot(event);
      if (!body || closed) return;
    };

    const handleSnapshot = async (event: Event) => {
      const body = readSnapshot(event);
      if (!body || closed) return;
      if (body.transactions) {
        await hydrateTransactions(body.transactions, { syncedAt: body.emittedAt });
      }
    };

    const handleHeartbeat = (event: Event) => {
      const body = readSnapshot(event);
      if (!body || closed) return;
    };

    const handleServerError = (event: Event) => {
      const body = readSnapshot(event);
      if (body?.message) {
        setError(body.message);
      }
    };

    source.addEventListener('connected', handleConnected);
    source.addEventListener('snapshot', handleSnapshot);
    source.addEventListener('update', handleSnapshot);
    source.addEventListener('heartbeat', handleHeartbeat);
    source.addEventListener('server-error', handleServerError);
    source.onerror = () => undefined;

    const fullRefreshTimer = window.setInterval(() => loadData(), fullRefreshMs);

    return () => {
      closed = true;
      window.clearInterval(fullRefreshTimer);
      source.close();
      if (eventSourceRef.current === source) {
        eventSourceRef.current = null;
      }
    };
  }, [hydrateTransactions, loadData, sseWatchMs, fullRefreshMs]);

  const partnerRows = transactions.filter(isPartnerTransaction);
  const partnerBusinessRows = partnerRows.filter((tx) => classifyLiveTx(tx) !== 'lifecycle');
  const partnerV1Rows = partnerBusinessRows.filter((tx) => tx.function_name === 'RecordIntegrationResult');
  const partnerLegacyRows = partnerBusinessRows.filter((tx) => tx.function_name === 'RecordEvent');
  const kulsOperationFilterOptions = useMemo(() => {
    const values = new Set(
      partnerBusinessRows
        .map((tx) => liveOperation(tx))
        .filter((value) => value && value !== '-')
    );
    return CONFIRMED_OPERATION_OPTIONS.filter((value) => values.has(value));
  }, [partnerBusinessRows]);
  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = partnerBusinessRows;

    if (isKuls) {
      if (kulsOrgFilter !== 'ALL') {
        rows = rows.filter((tx) => sourceOrgOf(tx) === kulsOrgFilter);
      }
      if (kulsOperationFilter !== 'ALL') {
        rows = rows.filter((tx) => liveOperation(tx) === kulsOperationFilter);
      }
    }

    if (!q) return rows;
    return rows.filter((tx) => [
      tx.transaction_id,
      tx.block_number,
      tx.sender,
      tx.receiver,
      tx.creator_msp_id,
      tx.chaincode_name,
      tx.chaincode_version,
      tx.function_name,
      tx.record_type,
      tx.business_id,
      tx.operation_type,
      tx.arg1,
      tx.arg2,
      tx.arg3,
      tx.arg8,
      tx.arg9
    ].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [query, partnerBusinessRows, isKuls, kulsOrgFilter, kulsOperationFilter]);

  const transactionsByBlock = useMemo(() => {
    const grouped = new Map<string, LivePrototypeTransaction[]>();
    transactions.forEach((tx) => {
      const key = tx.block_number || '-';
      grouped.set(key, [...(grouped.get(key) || []), tx]);
    });
    return grouped;
  }, [transactions]);

  const visibleBlockRows = useMemo<PrototypeBlockGroup[]>(() => {
    const grouped = new Map<string, LivePrototypeTransaction[]>();
    visibleRows.forEach((tx) => {
      const key = tx.block_number || '-';
      grouped.set(key, [...(grouped.get(key) || []), tx]);
    });
    return Array.from(grouped.entries())
      .map(([blockNumber, rows]) => {
        const partnerRowsForBlock = rows.slice().sort((a, b) => (b.transaction_index || 0) - (a.transaction_index || 0));
        const allRowsForBlock = (transactionsByBlock.get(blockNumber) || [])
          .slice()
          .sort((a, b) => (a.transaction_index || 0) - (b.transaction_index || 0));
        return {
          blockNumber,
          partnerRows: partnerRowsForBlock,
          allRows: allRowsForBlock,
          representative: partnerRowsForBlock[0] || allRowsForBlock[0]
        };
      })
      .filter((block): block is PrototypeBlockGroup => Boolean(block.representative))
      .sort((a, b) => blockNumberValue(b.blockNumber) - blockNumberValue(a.blockNumber));
  }, [visibleRows, transactionsByBlock]);

  const selectedBlock = selectedBlockNumber
    ? visibleBlockRows.find((block) => block.blockNumber === selectedBlockNumber) || null
    : null;

  const networkBlockHeight = networkStats?.latestBlockNumber || blockNumberValue(transactions[0]?.block_number);
  const networkTotalTransactions = networkStats?.totalTransactions || 0;
  const partnerPrototypeBlockCount = new Set(partnerBusinessRows.map((tx) => tx.block_number).filter(Boolean)).size;
  const todayKstKey = koreaDateKey(new Date().toISOString());
  const partnerTodayRows = partnerBusinessRows.filter((tx) => koreaDateKey(tx.block_created_at) === todayKstKey);
  const latestPartnerRecord = partnerBusinessRows[0] || null;
  const latestPartnerRecordProof = latestPartnerRecord ? proofs[latestPartnerRecord.transaction_id] : undefined;
  const latestV1Row = partnerV1Rows[0] || null;
  const latestV1Proof = latestV1Row ? proofs[latestV1Row.transaction_id] : undefined;
  const latestV1Payload = latestV1Row
    ? latestV1Proof?.rawPayload || legacyPayload(latestV1Row)
    : null;
  const latestScheduleRows = getRowsCount(latestV1Payload, 'best_schedule');
  const latestSimLogRows = getRowsCount(latestV1Payload, 'sim_log');
  const latestMovesCount = getMovesCount(latestV1Payload);
  const einsScenTypeRows = EINS_SCEN_TYPE_ORDER.map((scenType) => {
    const row = partnerBusinessRows.find((tx) => liveOperation(tx) === scenType) || null;
    const proof = row ? proofs[row.transaction_id] : undefined;
    const payload = row ? proof?.rawPayload || legacyPayload(row) : null;
    return {
      scenType,
      row,
      proof,
      payload,
      count: partnerBusinessRows.filter((tx) => liveOperation(tx) === scenType).length
    };
  });
  const kaistOperationRows = KAIST_OPERATION_ORDER.map((operation) => {
    const v1Row = partnerV1Rows.find((tx) => liveOperation(tx) === operation) || null;
    const legacyRow = partnerLegacyRows.find((tx) => liveOperation(tx) === operation) || null;
    const row = v1Row || legacyRow;
    const proof = v1Row ? proofs[v1Row.transaction_id] : undefined;
    const payload = row ? proof?.rawPayload || legacyPayload(row) : null;
    return {
      operation,
      v1Row,
      legacyRow,
      row,
      proof,
      payload
    };
  });
  const highlightedPartnerTxCount = partnerBusinessRows.filter((tx) => newTransactionIds.has(tx.transaction_id)).length;
  const highlightedPartnerBlockCount = new Set(
    partnerBusinessRows
      .filter((tx) => newBlockNumbers.has(tx.block_number))
      .map((tx) => tx.block_number)
  ).size;
  const hasPartnerHighlight = highlightedPartnerTxCount > 0 || highlightedPartnerBlockCount > 0;
  const newRowHighlightSx = {
    animation: 'prototypeNewRowFlash 4s ease-out',
    '@keyframes prototypeNewRowFlash': {
      '0%': { backgroundColor: 'rgba(46, 125, 50, 0.24)' },
      '18%': { backgroundColor: 'rgba(46, 125, 50, 0.16)' },
      '100%': { backgroundColor: 'transparent' }
    }
  };

  const kulsEinsRows = partnerBusinessRows.filter(isEinsTx);
  const kulsKaistRows = partnerBusinessRows.filter(isKaistTx);
  const kulsDirectRows = partnerBusinessRows.filter((tx) => sourceOrgOf(tx) === 'KULS');
  const kulsEinsTodayRows = kulsEinsRows.filter((tx) => koreaDateKey(tx.block_created_at) === todayKstKey);
  const kulsKaistTodayRows = kulsKaistRows.filter((tx) => koreaDateKey(tx.block_created_at) === todayKstKey);
  const latestPartnerSource = latestPartnerRecord ? sourceOrgOf(latestPartnerRecord) : null;
  const latestPartnerOperation = latestPartnerRecord ? liveOperation(latestPartnerRecord) : null;
  const kulsSourceSummaryRows = [
    { source: 'eINS', label: 'eINS 배차 최적화 결과', rows: kulsEinsRows },
    { source: 'KAIST', label: 'KAIST 야드 최적화 결과', rows: kulsKaistRows },
    { source: 'KULS', label: 'KULS 직접 기록 후보', rows: kulsDirectRows }
  ];

  const partnerPurpose = isKuls
    ? 'KULS는 현재 확보된 사실 기준으로 eINS와 KAIST가 KULS 대상으로 기록한 결과를 통합 조회하고 검증합니다.'
    : isKaist
    ? 'KAIST는 KULS 요청에 대한 assignment/retrieval/relocation 야드 최적화 결과를 보냅니다.'
    : 'eINS는 KULS DB 기반 배차 최적화 결과 payload를 최대한 기존 구조 그대로 보냅니다.';
  const requiredFields = isKuls
    ? ['eINS: scenario.scenario_id, scenario.scen_type', 'KAIST: request_id, operation']
    : isKaist
    ? ['request_id', 'operation']
    : ['scenario.scenario_id', 'scenario.scen_type'];
  const expectedFields = isKuls
    ? ['sourceOrg', 'targetOrg=KULS', 'recordType', 'businessId', 'operationType', 'payloadHash']
    : isKaist
    ? ['status', 'yard_id', 'yard_name', 'moves', 'summary', 'generated_at']
    : ['scenario', 'best', 'best_schedule', 'sim_log', 'metadata(선택)'];
  const requestExample = isKuls
    ? {
        kuls_screen_scope: 'targetOrg=KULS 관련 기록 조회',
        direct_kuls_invoke: '현재 확보된 사실만으로는 확정하지 않음',
        received_records: [
          {
            sourceOrg: 'EINS',
            recordType: 'EINS_DISPATCH_OPTIMIZATION_RESULT',
            businessIdType: 'scenario_id',
            operationType: 'Daily_Opt 또는 Emergency_Opt'
          },
          {
            sourceOrg: 'KAIST',
            recordType: 'KAIST_YARD_OPTIMIZATION_RESULT',
            businessIdType: 'request_id',
            operationType: 'assignment 또는 retrieval 또는 relocation'
          }
        ]
      }
    : isKaist
    ? {
        request_id: 'relocation-demo-001',
        operation: 'relocation',
        status: 'ok',
        yard_id: 1,
        yard_name: 'F',
        moves: [
          {
            step: 1,
            action: 'RELOCATION',
            container_id: '139',
            source: { kind: 'STACK', yard_id: 1, yard_name: 'F', bay: 1, row: 4, tier: 5 },
            destination: { kind: 'STACK', yard_id: 1, yard_name: 'F', bay: 2, row: 1, tier: 3 },
            metrics: { planner_step_idx: 0 },
            container_no: 'TIIU6137280'
          }
        ],
        summary: { reason: 'solved', move_count: 7 },
        generated_at: '2026-05-22T04:06:10.396303+00:00'
      }
    : {
        scenario: {
          scenario_id: 38,
          carrier_id: 'ktest0609',
          target_date: '2024-10-17',
          scenario_name: '테스트 베드, 대상 날짜: 2024-10-17',
          scen_type: 'Daily_Opt',
          state: '완료',
          best_testcase_id: 67216
        },
        best: {
          testcase_id: 67216,
          total_failed_cnt: 103,
          total_operation_time: 13527.059999999998,
          truck_count: 13,
          schedule_count: 286
        },
        best_schedule: {
          row_count: 286,
          rows: [
            { order_rank: 0, truck_id: '2580', order_id: '20241017143509072-016_EX', container_id: 'DRYU2499231' }
          ]
        },
        sim_log: {
          row_count: 1157,
          rows: [
            { record_id: 1, schedule_id: 643291, truck_id: '2580', type: 'MOVE', message: 'LOAD' }
          ]
        }
      };
  const mappingRows = isKuls
    ? [
        ['sourceOrg', 'eINS/KAIST 전용 endpoint와 API key 기준', 'EINS 또는 KAIST', 'Gateway'],
        ['targetOrg', '각 협력사 결과의 수신/검증 대상', 'KULS', 'Gateway'],
        ['recordType', '협력사 업무 유형에 따라 결정', 'EINS_DISPATCH_OPTIMIZATION_RESULT 또는 KAIST_YARD_OPTIMIZATION_RESULT', 'Gateway'],
        ['businessIdType', 'eINS는 scenario_id, KAIST는 request_id', 'scenario_id / request_id', 'Gateway'],
        ['businessId', 'eINS scenario.scenario_id 또는 KAIST request_id', '38 또는 relocation-demo-001', 'Gateway'],
        ['operationType', 'eINS scen_type 또는 KAIST operation', 'Daily_Opt / Emergency_Opt / assignment / retrieval / relocation', 'Gateway'],
        ['payloadHash', '각 협력사 payload 전체 canonical JSON SHA-256', 'sha256:...', 'Gateway'],
        ['payloadRef', 'PostgreSQL 원문 저장 위치', 'pg:integration_payloads/{payloadId}', 'Gateway'],
        ['recordId', 'sourceOrg/recordType/businessId/payloadHash 기반 생성', 'integration:EINS:... 또는 integration:KAIST:...', 'Gateway']
      ]
    : isKaist
    ? [
        ['sourceOrg', 'API key와 KAIST 전용 endpoint 기준', 'KAIST', 'Gateway'],
        ['targetOrg', 'KAIST 응답/통화 기준 결과 수신 대상', 'KULS', 'Gateway'],
        ['recordType', 'KAIST 결과 기록 유형', 'KAIST_YARD_OPTIMIZATION_RESULT', 'Gateway'],
        ['businessIdType', '요청/결과 식별자 유형', 'request_id', 'Gateway'],
        ['businessId', 'payload.request_id', 'relocation-demo-001', 'Gateway'],
        ['operationType', 'payload.operation을 trim + lower-case', 'relocation', 'Gateway'],
        ['payloadHash', '전체 payload canonical JSON SHA-256', 'sha256:...', 'Gateway'],
        ['payloadRef', 'PostgreSQL 원문 저장 위치', 'pg:integration_payloads/{payloadId}', 'Gateway'],
        ['recordId', 'sourceOrg/recordType/businessId/payloadHash 기반 생성', 'integration:KAIST:...', 'Gateway']
      ]
    : [
        ['sourceOrg', 'API key와 eINS 전용 endpoint 기준', 'EINS', 'Gateway'],
        ['targetOrg', 'eINS 회신 기준 KULS DB/기록 대상', 'KULS', 'Gateway'],
        ['recordType', 'eINS 배차 최적화 결과 기록 유형', 'EINS_DISPATCH_OPTIMIZATION_RESULT', 'Gateway'],
        ['businessIdType', '실행 식별자 유형', 'scenario_id', 'Gateway'],
        ['businessId', 'payload.scenario.scenario_id', '38', 'Gateway'],
        ['operationType', 'payload.scenario.scen_type 원문 유지', 'Daily_Opt 또는 Emergency_Opt', 'Gateway'],
        ['payloadHash', '전체 payload canonical JSON SHA-256', 'sha256:...', 'Gateway'],
        ['payloadRef', 'PostgreSQL 원문 저장 위치', 'pg:integration_payloads/{payloadId}', 'Gateway'],
        ['recordId', 'sourceOrg/recordType/businessId/payloadHash 기반 생성', 'integration:EINS:...', 'Gateway']
      ];
  const displayRows = isKuls
    ? [
        ['sourceOrg / targetOrg', '어느 협력사의 결과가 KULS 대상으로 기록되었는지 보여줍니다.', '트랜잭션 트래킹의 발신 → 수신 컬럼'],
        ['recordType', 'eINS 배차 최적화 결과인지 KAIST 야드 최적화 결과인지 구분합니다.', '기록 유형, 대시보드 기관별 집계'],
        ['businessId', 'eINS는 scenario_id, KAIST는 request_id 기준으로 조회합니다.', '업무 ID 컬럼과 상세 모달'],
        ['operationType', 'eINS는 scen_type, KAIST는 operation입니다.', '업무 구분 컬럼과 필터 후보'],
        ['ledgerStatus', '트랜잭션이 블록에 포함되어 원장에 반영되었는지 보여줍니다.', 'COMMITTED / 기록 완료'],
        ['indexStatus', '원장 기록이 WebApp PostgreSQL 색인에 반영되었는지 보여줍니다.', 'INDEXED / WebApp 반영 완료'],
        ['payloadHash', 'KULS가 보는 결과 원문이 원장 hash와 일치하는지 검증하는 증거입니다.', '무결성 칩과 상세 hash 검증'],
        ['recordStatus', '`SUBMITTED`는 업무 record 내부 제출 상태로만 표시하고 원장 상태와 섞지 않습니다.', '상세 모달의 업무 상태']
      ]
    : isKaist
    ? [
        ['businessId', '`request_id` 기준 검색/조회 키입니다.', '트랜잭션 트래킹 탭의 요청 ID, 상세 모달의 요청 ID'],
        ['operationType', '`assignment`, `retrieval`, `relocation` 구분입니다.', '작업 / Operation 컬럼과 필터 후보'],
        ['ledgerStatus', '트랜잭션이 블록에 포함되어 원장에 반영되었는지 보여줍니다.', 'COMMITTED / 기록 완료'],
        ['indexStatus', '원장 기록이 WebApp PostgreSQL 색인에 반영되었는지 보여줍니다.', 'INDEXED / WebApp 반영 완료'],
        ['payloadHash', 'KAIST 결과 payload 전체의 무결성 증거입니다.', 'Payload 증거 / Hash Check'],
        ['recordStatus', '`SUBMITTED`는 업무 record 내부 제출 상태로만 표시하고 원장 상태와 섞지 않습니다.', '상세 모달의 업무 상태'],
        ['txId', 'Fabric commit 이후 생성된 트랜잭션 ID입니다.', '상세 모달과 블록 위치 조회'],
        ['blockNumber', '해당 기록이 들어간 블록 번호입니다.', '블록 익스플로러 탭의 위치']
      ]
    : [
        ['businessId', '`scenario.scenario_id` 기준 검색/조회 키입니다.', '트랜잭션 트래킹 탭의 Run ID, 상세 모달의 scenario_id'],
        ['operationType', '`scenario.scen_type` 기준 구분값입니다.', 'Daily_Opt/Emergency_Opt 필터 후보'],
        ['ledgerStatus', '트랜잭션이 블록에 포함되어 원장에 반영되었는지 보여줍니다.', 'COMMITTED / 기록 완료'],
        ['indexStatus', '원장 기록이 WebApp PostgreSQL 색인에 반영되었는지 보여줍니다.', 'INDEXED / WebApp 반영 완료'],
        ['payloadHash', 'scenario/best/best_schedule/sim_log 전체의 무결성 증거입니다.', 'Payload 증거 / Hash Check'],
        ['recordStatus', '`SUBMITTED`는 업무 record 내부 제출 상태로만 표시하고 원장 상태와 섞지 않습니다.', '상세 모달의 업무 상태'],
        ['txId', 'Fabric commit 이후 생성된 트랜잭션 ID입니다.', '상세 모달과 블록 위치 조회'],
        ['blockNumber', '해당 기록이 들어간 블록 번호입니다.', '블록 익스플로러 탭의 위치']
      ];
  const transitionRows = isKuls
    ? [
        ['1. 현재 확보된 흐름', 'eINS는 KULS DB 기반 배차 최적화 결과를 기록하고, KAIST는 KULS 요청 기반 야드 최적화 결과를 기록합니다. 두 흐름 모두 현재 targetOrg는 KULS입니다.'],
        ['2. KULS의 위치', '현재 사실 기준으로 KULS가 직접 블록체인 invoke를 해야 한다고 확정하지 않았습니다. 우선 KULS는 eINS/KAIST 결과를 조회하고 검증하는 통합 화면으로 둡니다.'],
        ['3. 공통 체인코드 초안', 'eINS와 KAIST payload는 Gateway에서 `RecordIntegrationResult(recordJSON)` 공통 record로 변환되어 원장에 기록됩니다.'],
        ['4. KULS 화면에서 보는 것', 'KULS는 `targetOrg=KULS` 또는 KULS 관련 기록을 기준으로 eINS/KAIST 결과를 함께 봅니다.'],
        ['5. 무결성 검증', 'WebApp은 PostgreSQL 원문 payload를 canonical hash로 재계산하고, 원장 payloadHash와 비교해 KULS 화면에 검증 상태를 표시합니다.'],
        ['6. 후속 가능성', 'KULS request 자체를 블록체인에 기록할지, KULS 직접 invoke endpoint를 둘지는 별도 안건입니다. 지금 화면은 그 결정을 확정하지 않습니다.']
      ]
    : isKaist
    ? [
        ['1. 기존 명세', 'generic invoke 방식으로 `chaincode`, `function`, `args[]`를 직접 넣는 `RecordEvent` 테스트 구조였습니다. KAIST 테스트는 `KAIST_OPTIMIZE`, `pipelineId`, dummy `prevTxId`, `sender=KAIST`, `receiver=KULS`를 넣어 진행되었습니다.'],
        ['2. KAIST 자료 확인', 'KAIST DOCX guideline과 실제 테스트 tx에서 `request_id`, `operation`, `status`, `yard_id`, `moves`, `summary`, `generated_at` 형태의 결과 payload가 확인되었습니다.'],
        ['3. 공통 체인코드 초안', '협력사가 `args[]`를 직접 맞추지 않도록 `RecordIntegrationResult(recordJSON)` 공통 함수를 만들었습니다. 이 함수는 원장 증거에 필요한 공통 record만 받습니다.'],
        ['4. KAIST가 실제 보내는 것', 'KAIST는 전용 endpoint로 야드 최적화 결과 payload를 보냅니다. 최소 필수값은 `request_id`, `operation`입니다.'],
        ['5. 인제대 Gateway 처리', 'Gateway가 API key로 KAIST를 확인하고, payload를 저장하고, hash를 계산하고, `request_id`와 `operation`을 공통 record 필드로 매핑합니다.'],
        ['6. 최종 기록', 'Gateway가 공통 record를 `RecordIntegrationResult(recordJSON)`로 invoke하고, commit 후 WebApp에서 txId, blockNumber, businessId, operationType, payloadHash로 조회합니다.']
      ]
    : [
        ['1. 기존 명세', 'generic invoke 방식으로 `chaincode`, `function`, `args[]`를 직접 넣는 `RecordEvent` 중심 구조였습니다. 기존 5단계 pipeline/stage/prevTxId 가정은 eINS standalone 구조와 맞지 않았습니다.'],
        ['2. eINS 샘플 확인', 'eINS 샘플과 회신에서 `scenario`, `best`, `best_schedule`, `sim_log` 구조가 확인되었습니다. eINS는 KULS DB 기반 payload 구조를 최대한 유지하기를 원합니다.'],
        ['3. 공통 체인코드 초안', '협력사가 `args[]`를 직접 맞추지 않도록 `RecordIntegrationResult(recordJSON)` 공통 함수를 만들었습니다. 이 함수는 원장 증거에 필요한 공통 record만 받습니다.'],
        ['4. eINS가 실제 보내는 것', 'eINS는 전용 endpoint로 배차 최적화 결과 payload를 보냅니다. 최소 필수값은 `scenario.scenario_id`, `scenario.scen_type`입니다.'],
        ['5. 인제대 Gateway 처리', 'Gateway가 API key로 EINS를 확인하고, payload를 저장하고, hash를 계산하고, `scenario_id`와 `scen_type`을 공통 record 필드로 매핑합니다.'],
        ['6. 최종 기록', 'Gateway가 공통 record를 `RecordIntegrationResult(recordJSON)`로 invoke하고, commit 후 WebApp에서 txId, blockNumber, businessId, operationType, payloadHash로 조회합니다.']
      ];
  const gatewayFlowRows = isKuls
    ? [
        ['1', '협력사 결과 수신', 'eINS/KAIST 전용 endpoint로 업무 payload가 들어옵니다.'],
        ['2', 'Gateway 변환', '각 협력사 payload를 sourceOrg=EINS 또는 KAIST, targetOrg=KULS인 공통 record로 매핑합니다.'],
        ['3', '저장/Hash', '원문 payload를 PostgreSQL에 저장하고 canonical JSON SHA-256 hash를 계산합니다.'],
        ['4', 'Invoke', 'supplychain.RecordIntegrationResult(recordJSON)로 원장에 hash-ref 증거를 기록합니다.'],
        ['5', '색인', 'WebApp backend가 commit된 tx/block을 PostgreSQL index에서 읽습니다.'],
        ['6', 'KULS 표시', 'KULS 화면은 targetOrg=KULS 관련 기록을 대시보드, 트랜잭션, 블록 익스플로러에 표시합니다.']
      ]
    : [
        ['1', '요청 수신', t("{0} endpoint로 JSON payload를 받습니다.", [partnerLabel])],
        ['2', '인증', t("X-API-Key로 sourceOrg={0}를 판정합니다.", [sourceOrg])],
        ['3', '검증', t("{0} 존재 여부와 형식을 확인합니다.", [requiredFields.join(', ')])],
        ['4', '매핑', 'partner payload에서 공통 record 필드를 만듭니다.'],
        ['5', '저장', '원문 payload와 canonical payload를 PostgreSQL에 저장합니다.'],
        ['6', 'Hash', 'canonical JSON 기준 SHA-256 payloadHash를 계산합니다.'],
        ['7', 'Invoke', 'supplychain.RecordIntegrationResult(recordJSON)를 호출합니다.'],
        ['8', '표시', 'commit 후 WebApp에서 txId/block/hash를 조회합니다.']
      ];
  const successResponseExample = isKuls
    ? {
        screen: 'KULS Integration Prototype',
        scope: 'targetOrg=KULS related records',
        sourceBreakdown: {
          eINS: kulsEinsRows.length,
          KAIST: kulsKaistRows.length,
          KULS: kulsDirectRows.length
        },
        latestRecord: latestPartnerRecord ? {
          sourceOrg: sourceOrgOf(latestPartnerRecord),
          targetOrg: targetOrgOf(latestPartnerRecord),
          recordType: liveRecordType(latestPartnerRecord),
          businessId: liveBusinessId(latestPartnerRecord),
          operationType: liveOperation(latestPartnerRecord),
          blockNumber: latestPartnerRecord.block_number,
          txId: latestPartnerRecord.transaction_id,
          payloadHashStatus: liveEvidenceNote(latestPartnerRecord, latestPartnerRecordProof)
        } : null
      }
    : {
        success: true,
        message: 'Integration result recorded successfully',
        data: {
          duplicate: false,
          recordId: `integration:${sourceOrg}:...`,
          correlationId: `integration-${sourceOrg.toLowerCase()}-...`,
          txId: '...',
          blockNumber: isKaist ? 326 : 325,
          status: 'COMMITTED',
          sourceOrg,
          targetOrg: 'KULS',
          recordType: isKaist ? 'KAIST_YARD_OPTIMIZATION_RESULT' : 'EINS_DISPATCH_OPTIMIZATION_RESULT',
          businessIdType: isKaist ? 'request_id' : 'scenario_id',
          businessId: isKaist ? 'relocation-demo-001' : '38',
          operationType: isKaist ? 'relocation' : 'Daily_Opt',
          payloadHash: 'sha256:...',
          payloadStorageType: 'POSTGRES'
        }
      };
  const kulsFilterBar = isKuls ? (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        p: 1.5,
        bgcolor: '#fafafa',
        borderColor: '#e0e0e0'
      }}
    >
      <Stack spacing={1.25}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2.5}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
        >
          <FormControl component="fieldset" sx={{ minWidth: 220 }}>
            <FormLabel component="legend" sx={{ mb: 0.5, fontWeight: 'bold', fontSize: '0.82rem' }}>{t("기관")}</FormLabel>
            <RadioGroup
              row
              value={kulsOrgFilter}
              onChange={(event) => setKulsOrgFilter(event.target.value)}
              sx={{ flexWrap: 'wrap' }}
            >
              <FormControlLabel value="ALL" control={<Radio size="small" />} label={t("전체")} />
              <FormControlLabel value="EINS" control={<Radio size="small" />} label={t("eINS")} />
              <FormControlLabel value="KAIST" control={<Radio size="small" />} label={t("KAIST")} />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ flex: 1, minWidth: 0 }}>
            <FormLabel component="legend" sx={{ mb: 0.5, fontWeight: 'bold', fontSize: '0.82rem' }}>{t("업무 구분")}</FormLabel>
            <RadioGroup
              row
              value={kulsOperationFilter}
              onChange={(event) => setKulsOperationFilter(event.target.value)}
              sx={{
                gap: 0.5,
                flexWrap: 'wrap',
                '& .MuiFormControlLabel-root': {
                  mr: 1,
                  whiteSpace: 'nowrap'
                }
              }}
            >
              <FormControlLabel value="ALL" control={<Radio size="small" />} label={t("전체")} />
              {kulsOperationFilterOptions.map((operation) => (
                <FormControlLabel
                  key={operation}
                  value={operation}
                  control={<Radio size="small" />}
                  label={ui(operation)}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary" fontWeight="bold">{t("현재 필터")}</Typography>
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={kulsOrgFilter === 'ALL' ? t("기관: 전체") : t("기관: {0}", [kulsOrgFilter])}
          />
          <Chip
            size="small"
            variant="outlined"
            color="secondary"
            label={kulsOperationFilter === 'ALL' ? t("업무: 전체") : t("업무: {0}", [kulsOperationFilter])}
          />
          <Button
            variant="text"
            size="small"
            disabled={kulsOrgFilter === 'ALL' && kulsOperationFilter === 'ALL'}
            onClick={() => {
              setKulsOrgFilter('ALL');
              setKulsOperationFilter('ALL');
            }}
          >{t("초기화")}</Button>
        </Stack>
      </Stack>
    </Paper>
  ) : null;

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0, px: { xs: 1.5, md: 2.5, xl: 3 }, py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1976d2', fontWeight: 'bold' }}>
          <IntegrationIcon sx={{ fontSize: 34 }} />{t("협력사 연동 프로토타입")}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          {user.orgName}{" "}{t("계정에서 보는 체인코드 v1.1 업그레이드 이후 실측 기록입니다. Block #")}{PROTOTYPE_FROM_BLOCK}{" "}{t("이후 WebApp 색인값만 표시합니다.")}</Typography>
      </Box>

      {hasPartnerHighlight && (
        <Alert severity="success" sx={{ mb: 2 }}>{t("새 기록")}{" "}{highlightedPartnerTxCount}{t("건이 반영되었습니다.")}</Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{uiError(error)}</Alert>}

      <Tabs value={tab} onChange={(_, next) => setTab(next)} variant="scrollable" allowScrollButtonsMobile sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tab label={t("대시보드")} />
        <Tab label={t("트랜잭션 트래킹")} />
        <Tab label={t("블록 익스플로러")} />
        <Tab label={t("API/매핑 / Mapping")} />
      </Tabs>

      {loading ? (
        <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
          <CircularProgress />
          <Typography>{t("실측 트랜잭션 동기화 중 / Loading indexed transactions")}</Typography>
        </Stack>
      ) : (
        <>
          {tab === 0 && (
            <Stack spacing={3}>
              {isKuls ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <DashboardMetricCard
                      label={t("전체 블록 높이")}
                      value={networkBlockHeight ? `#${networkBlockHeight}` : '-'}
                      caption={networkTotalTransactions ? t("전체 트랜잭션 {0}건", [networkTotalTransactions]) : t("네트워크 전체 최신 블록")}
                      color="#1976d2"
                      gradient="linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
                      icon={<PublicIcon sx={{ fontSize: 44 }} />}
                      highlight={hasPartnerHighlight}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <DashboardMetricCard
                      label={t("eINS 기록")}
                      value={t("{0}건", [kulsEinsRows.length])}
                      caption={t("v1 {0} / 호환 {1}", [kulsEinsRows.filter((tx) => tx.function_name === 'RecordIntegrationResult').length, kulsEinsRows.filter((tx) => tx.function_name === 'RecordEvent').length])}
                      color="#26a69a"
                      gradient="linear-gradient(135deg, #26a69a 0%, #00897b 100%)"
                      icon={<IntegrationIcon sx={{ fontSize: 44 }} />}
                      highlight={kulsEinsRows.some((tx) => newTransactionIds.has(tx.transaction_id))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <DashboardMetricCard
                      label={t("KAIST 기록")}
                      value={t("{0}건", [kulsKaistRows.length])}
                      caption={t("v1 {0} / 호환 {1}", [kulsKaistRows.filter((tx) => tx.function_name === 'RecordIntegrationResult').length, kulsKaistRows.filter((tx) => tx.function_name === 'RecordEvent').length])}
                      color="#5c6bc0"
                      gradient="linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)"
                      icon={<IntegrationIcon sx={{ fontSize: 44 }} />}
                      highlight={kulsKaistRows.some((tx) => newTransactionIds.has(tx.transaction_id))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <DashboardMetricCard
                      label={t("오늘 eINS")}
                      value={t("{0}건", [kulsEinsTodayRows.length])}
                      caption={t("오늘 KST commit된 eINS → KULS 기록")}
                      color="#ff9800"
                      gradient="linear-gradient(135deg, #ff9800 0%, #f57c00 100%)"
                      icon={<TodayIcon sx={{ fontSize: 44 }} />}
                      highlight={kulsEinsTodayRows.some((tx) => newTransactionIds.has(tx.transaction_id))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <DashboardMetricCard
                      label={t("오늘 KAIST")}
                      value={t("{0}건", [kulsKaistTodayRows.length])}
                      caption={t("오늘 KST commit된 KAIST → KULS 기록")}
                      color="#ef5350"
                      gradient="linear-gradient(135deg, #ef5350 0%, #d32f2f 100%)"
                      icon={<TodayIcon sx={{ fontSize: 44 }} />}
                      highlight={kulsKaistTodayRows.some((tx) => newTransactionIds.has(tx.transaction_id))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <DashboardMetricCard
                      label={t("최근 기록")}
                      value={formatRelativeTimeKo(latestPartnerRecord?.block_created_at)}
                      caption={latestPartnerRecord ? t("{0} / {1} / Block #{2}", [latestPartnerSource, latestPartnerOperation, latestPartnerRecord.block_number]) : t("아직 기록 없음")}
                      color="#7b1fa2"
                      gradient="linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)"
                      icon={<ScheduleIcon sx={{ fontSize: 44 }} />}
                      highlight={hasPartnerHighlight}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <DashboardMetricCard
                      label={t("전체 블록 높이")}
                      value={networkBlockHeight ? `#${networkBlockHeight}` : '-'}
                      caption={networkTotalTransactions ? t("전체 트랜잭션 {0}건", [networkTotalTransactions]) : t("네트워크 전체 최신 블록")}
                      color="#1976d2"
                      gradient="linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
                      icon={<PublicIcon sx={{ fontSize: 48 }} />}
                      highlight={hasPartnerHighlight}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DashboardMetricCard
                      label={t("{0} 프로토타입 기록", [ui(partnerRecordScopeLabel)])}
                      value={t("{0}건", [partnerBusinessRows.length])}
                      caption={t("{0}개 블록 / v1 {1} / 호환 {2}", [partnerPrototypeBlockCount, partnerV1Rows.length, partnerLegacyRows.length])}
                      color="#26a69a"
                      gradient="linear-gradient(135deg, #26a69a 0%, #00897b 100%)"
                      icon={<IntegrationIcon sx={{ fontSize: 48 }} />}
                      highlight={highlightedPartnerTxCount > 0}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DashboardMetricCard
                      label={t("오늘 기록")}
                      value={t("{0}건", [partnerTodayRows.length])}
                      caption={t("오늘 KST commit된 {0} 기록", [ui(partnerRecordScopeLabel)])}
                      color="#ff9800"
                      gradient="linear-gradient(135deg, #ff9800 0%, #f57c00 100%)"
                      icon={<TodayIcon sx={{ fontSize: 48 }} />}
                      highlight={partnerTodayRows.some((tx) => newTransactionIds.has(tx.transaction_id))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DashboardMetricCard
                      label={t("최근 기록")}
                      value={formatRelativeTimeKo(latestPartnerRecord?.block_created_at)}
                      caption={latestPartnerRecord ? t("{0} / Block #{1}", [liveStatus(latestPartnerRecord), latestPartnerRecord.block_number]) : t("아직 기록 없음")}
                      color="#7b1fa2"
                      gradient="linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)"
                      icon={<ScheduleIcon sx={{ fontSize: 48 }} />}
                      highlight={hasPartnerHighlight}
                    />
                  </Grid>
                </Grid>
              )}

              {isKuls ? (
                <>
                  <Grid container spacing={3}>
                    <Grid item xs={12} lg={7}>
                      <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>{t("KULS 수신 결과 출처별 요약")}</Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell><strong>{t("출처")}</strong></TableCell>
                                <TableCell><strong>{t("업무 성격")}</strong></TableCell>
                                <TableCell align="right"><strong>{t("전체")}</strong></TableCell>
                                <TableCell align="right"><strong>{t("v1")}</strong></TableCell>
                                <TableCell align="right"><strong>{t("기존 호환")}</strong></TableCell>
                                <TableCell><strong>{t("최신 블록")}</strong></TableCell>
                                <TableCell><strong>{t("최신 업무 구분")}</strong></TableCell>
                                <TableCell><strong>{t("상태")}</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {kulsSourceSummaryRows.map(({ source, label, rows }) => {
                                const latestRow = rows[0] || null;
                                const v1Count = rows.filter((tx) => tx.function_name === 'RecordIntegrationResult').length;
                                const legacyCount = rows.filter((tx) => tx.function_name === 'RecordEvent').length;
                                return (
                                  <TableRow
                                    hover={Boolean(latestRow)}
                                    key={source}
                                    sx={{
                                      cursor: latestRow ? 'pointer' : 'default',
                                      ...(latestRow && newTransactionIds.has(latestRow.transaction_id) ? newRowHighlightSx : {})
                                    }}
                                    onClick={() => latestRow && setSelectedTx(latestRow)}
                                  >
                                    <TableCell sx={{ fontWeight: 700 }}>{ui(source)}</TableCell>
                                    <TableCell>{ui(label)}</TableCell>
                                    <TableCell align="right">{rows.length}</TableCell>
                                    <TableCell align="right">{v1Count}</TableCell>
                                    <TableCell align="right">{legacyCount}</TableCell>
                                    <TableCell>{latestRow ? `#${latestRow.block_number}` : '-'}</TableCell>
                                    <TableCell>{latestRow ? liveOperation(latestRow) : '-'}</TableCell>
                                    <TableCell>
                                      {latestRow ? (
                                        <Stack spacing={0.5} alignItems="flex-start">
                                          <LedgerStatusChip tx={latestRow} />
                                        </Stack>
                                      ) : '-'}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} lg={5}>
                      <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>{t("최근 KULS 관련 기록")}</Typography>
                        {latestPartnerRecord ? (
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}><DetailField label={t("기록시각")} value={formatTime(latestPartnerRecord.block_created_at)} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("블록")} value={`#${latestPartnerRecord.block_number}`} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("발신 → 수신")} value={`${sourceOrgOf(latestPartnerRecord) || '-'} → ${targetOrgOf(latestPartnerRecord) || '-'}`} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("기록 유형")} value={liveRecordType(latestPartnerRecord)} /></Grid>
                            <Grid item xs={12}><DetailField label={t("업무 ID")} value={liveBusinessId(latestPartnerRecord)} mono /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("업무 구분")} value={liveOperation(latestPartnerRecord)} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("Payload 크기")} value={formatBytes(livePayloadSizeBytes(latestPartnerRecord))} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("원장 상태")} value={ledgerStatusLabel(latestPartnerRecord)} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("무결성 검증")} value={integrityStatusLabel(latestPartnerRecord, latestPartnerRecordProof)} /></Grid>
                          </Grid>
                        ) : (
                          <Alert severity="info">{t("아직 KULS 관련 prototype 기록이 없습니다.")}</Alert>
                        )}
                      </Paper>
                    </Grid>

                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2.5 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>{t("최근 기록 목록")}</Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell><strong>{t("블록")}</strong></TableCell>
                                <TableCell><strong>{t("발신 → 수신")}</strong></TableCell>
                                <TableCell><strong>{t("기록 유형")}</strong></TableCell>
                                <TableCell><strong>{t("업무 ID")}</strong></TableCell>
                                <TableCell><strong>{t("업무 구분")}</strong></TableCell>
                                <TableCell><strong>{t("Payload")}</strong></TableCell>
                                <TableCell><strong>{t("상태")}</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {partnerBusinessRows.slice(0, 8).map((tx) => {
                                return (
                                  <TableRow
                                    hover
                                    key={tx.transaction_id}
                                    sx={{
                                      cursor: 'pointer',
                                      ...(newTransactionIds.has(tx.transaction_id) ? newRowHighlightSx : {})
                                    }}
                                    onClick={() => setSelectedTx(tx)}
                                  >
                                    <TableCell>#{tx.block_number}</TableCell>
                                    <TableCell>{sourceOrgOf(tx) || '-'} → {targetOrgOf(tx) || '-'}</TableCell>
                                    <TableCell><Chip size="small" color={categoryColor(tx)} label={liveRecordType(tx)} /></TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{shortBusinessId(liveBusinessId(tx))}</TableCell>
                                    <TableCell>{liveOperation(tx)}</TableCell>
                                    <TableCell>{formatBytes(livePayloadSizeBytes(tx))}</TableCell>
                                    <TableCell>
                                      <Stack spacing={0.5} alignItems="flex-start">
                                        <LedgerStatusChip tx={tx} />
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Alert severity="info">{t("KULS 화면은 현재 확보된 사실 기준으로 targetOrg=KULS 관련 기록을 통합 조회하는 검증 화면입니다. KULS가 직접 블록체인에 request를 기록하는 구조는 아직 확정하지 않았습니다.")}</Alert>
                </>
              ) : isKaist ? (
                <>
                  <Grid container spacing={3}>
                    <Grid item xs={12} lg={7}>
                      <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>{t("KAIST v1 작업별 기록")}</Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell><strong>{t("작업 구분")}</strong></TableCell>
                                <TableCell><strong>{t("v1 상태")}</strong></TableCell>
                                <TableCell><strong>{t("최신 블록")}</strong></TableCell>
                                <TableCell><strong>{t("요청 ID")}</strong></TableCell>
                                <TableCell align="right"><strong>{t("moves")}</strong></TableCell>
                                <TableCell><strong>{t("Payload 크기")}</strong></TableCell>
                                <TableCell><strong>{t("상태")}</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {kaistOperationRows.map(({ operation, v1Row, legacyRow, row, payload }) => (
                                <TableRow
                                  hover={Boolean(row)}
                                  key={operation}
                                  sx={{
                                    cursor: row ? 'pointer' : 'default',
                                    ...(row && newTransactionIds.has(row.transaction_id) ? newRowHighlightSx : {})
                                  }}
                                  onClick={() => row && setSelectedTx(row)}
                                >
                                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{operation}</TableCell>
                                  <TableCell>
                                    {v1Row ? (
                                      <Chip size="small" color="success" label={t("v1 기록")} />
                                    ) : legacyRow ? (
                                      <Chip size="small" color="info" label={t("기존 호환만 있음")} />
                                    ) : (
                                      <Chip size="small" variant="outlined" label={t("미수신")} />
                                    )}
                                  </TableCell>
                                  <TableCell>{row ? `#${row.block_number}` : '-'}</TableCell>
                                  <TableCell sx={{ minWidth: 220, fontFamily: 'monospace' }}>{row ? liveBusinessId(row) : '-'}</TableCell>
                                  <TableCell align="right">{payload ? getMovesCount(payload) ?? '-' : '-'}</TableCell>
                                  <TableCell>{row ? formatBytes(livePayloadSizeBytes(row)) : '-'}</TableCell>
                                  <TableCell>
                                    {row ? (
                                      <Stack spacing={0.5} alignItems="flex-start">
                                        <LedgerStatusChip tx={row} />
                                      </Stack>
                                    ) : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} lg={5}>
                      <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>{t("최근 KAIST v1 기록")}</Typography>
                        {latestV1Row ? (
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}><DetailField label={t("기록시각")} value={formatTime(latestV1Row.block_created_at)} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("블록")} value={`#${latestV1Row.block_number}`} /></Grid>
                            <Grid item xs={12}><DetailField label={t("Tx ID")} value={shortHash(latestV1Row.transaction_id)} mono /></Grid>
                            <Grid item xs={12}><DetailField label={t("요청 ID")} value={liveBusinessId(latestV1Row)} mono /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("작업 구분")} value={liveOperation(latestV1Row)} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("Payload 크기")} value={formatBytes(livePayloadSizeBytes(latestV1Row))} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("야드")} value={[latestV1Payload?.yard_id, latestV1Payload?.yard_name].filter(Boolean).join(' / ') || '-'} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("moves 수")} value={latestMovesCount ?? '-'} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("원장 상태")} value={ledgerStatusLabel(latestV1Row)} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("무결성 검증")} value={integrityStatusLabel(latestV1Row, latestV1Proof)} /></Grid>
                          </Grid>
                        ) : (
                          <Alert severity="info">{t("아직 KAIST v1 기록이 없습니다.")}</Alert>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>

                  <Alert severity="info">{t("KAIST v1 기록은 인제대 내부 검증 데이터입니다. 기존 RecordEvent 기록은 업그레이드 전후 호환성 확인 기록으로 분리해서 봅니다.")}</Alert>
                </>
              ) : (
                <>
                  <Grid container spacing={3}>
                    <Grid item xs={12} lg={5}>
                      <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>{t("최근 eINS v1 기록")}</Typography>
                        {latestV1Row ? (
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}><DetailField label={t("기록시각")} value={formatTime(latestV1Row.block_created_at)} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("블록")} value={`#${latestV1Row.block_number}`} /></Grid>
                            <Grid item xs={12}><DetailField label={t("Tx ID")} value={shortHash(latestV1Row.transaction_id)} mono /></Grid>
                            <Grid item xs={12}><DetailField label={t("Run ID")} value={liveBusinessId(latestV1Row)} mono /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("서비스/시나리오 구분")} value={liveOperation(latestV1Row)} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("Payload 크기")} value={formatBytes(livePayloadSizeBytes(latestV1Row))} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("best_schedule rows")} value={latestScheduleRows ?? '-'} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("sim_log rows")} value={latestSimLogRows ?? '-'} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("원장 상태")} value={ledgerStatusLabel(latestV1Row)} /></Grid>
                            <Grid item xs={12} sm={6}><DetailField label={t("무결성 검증")} value={integrityStatusLabel(latestV1Row, latestV1Proof)} /></Grid>
                          </Grid>
                        ) : (
                          <Alert severity="info">{t("아직 eINS v1 기록이 없습니다.")}</Alert>
                        )}
                      </Paper>
                    </Grid>

                    <Grid item xs={12} lg={7}>
                      <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>{t("eINS 서비스/시나리오 구분별 기록")}</Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell><strong>{t("구분값")}</strong></TableCell>
                                <TableCell><strong>{t("해석")}</strong></TableCell>
                                <TableCell align="right"><strong>{t("건수")}</strong></TableCell>
                                <TableCell><strong>{t("최신 블록")}</strong></TableCell>
                                <TableCell align="right"><strong>{t("schedule rows")}</strong></TableCell>
                                <TableCell align="right"><strong>{t("log rows")}</strong></TableCell>
                                <TableCell><strong>{t("Payload 크기")}</strong></TableCell>
                                <TableCell><strong>{t("상태")}</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {einsScenTypeRows.map(({ scenType, row, payload, count }) => (
                                <TableRow
                                  hover={Boolean(row)}
                                  key={scenType}
                                  sx={{
                                    cursor: row ? 'pointer' : 'default',
                                    ...(row && newTransactionIds.has(row.transaction_id) ? newRowHighlightSx : {})
                                  }}
                                  onClick={() => row && setSelectedTx(row)}
                                >
                                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{scenType}</TableCell>
                                  <TableCell>{t("운영 기록 대상")}</TableCell>
                                  <TableCell align="right">{count}</TableCell>
                                  <TableCell>{row ? `#${row.block_number}` : '-'}</TableCell>
                                  <TableCell align="right">{payload ? getRowsCount(payload, 'best_schedule') ?? '-' : '-'}</TableCell>
                                  <TableCell align="right">{payload ? getRowsCount(payload, 'sim_log') ?? '-' : '-'}</TableCell>
                                  <TableCell>{row ? formatBytes(livePayloadSizeBytes(row)) : '-'}</TableCell>
                                  <TableCell>
                                    {row ? (
                                      <Stack spacing={0.5} alignItems="flex-start">
                                        <LedgerStatusChip tx={row} />
                                      </Stack>
                                    ) : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Alert severity="info">{t("현재 eINS v1 기록은 인제대 내부 검증 데이터입니다. 실제 eINS 운영 invoke 수신 전까지는 표시 방식과 매핑 검증용으로 해석합니다.")}</Alert>
                </>
              )}
            </Stack>
          )}

          {tab === 1 && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField
                    size="small"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={t("{0} txId, block, source, businessId, operation 검색", [partnerLabel])}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                    sx={{ minWidth: 360, flexGrow: 1 }}
                  />
                  {kulsFilterBar}
                  <Chip label={t("{0}건", [visibleRows.length])} color="primary" variant="outlined" />
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" sx={{ minWidth: 940 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell><strong>{t("기록시각")}</strong></TableCell>
                        <TableCell><strong>{t("블록")}</strong></TableCell>
                        <TableCell><strong>{t("Tx ID")}</strong></TableCell>
                        {isKuls && <TableCell><strong>{t("발신 → 수신")}</strong></TableCell>}
                        <TableCell><strong>{t("기록 유형")}</strong></TableCell>
                        <TableCell><strong>{isKuls ? t("업무 ID") : isKaist ? t("요청 ID") : t("Run ID")}</strong></TableCell>
                        <TableCell><strong>{isKuls ? t("업무 구분") : isKaist ? t("작업 구분") : t("서비스/시나리오 구분")}</strong></TableCell>
                        <TableCell><strong>{t("Payload 크기")}</strong></TableCell>
                        <TableCell><strong>{t("상태")}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleRows.map((tx) => {
                        const isNew = newTransactionIds.has(tx.transaction_id);
                        return (
                          <TableRow
                            hover
                            key={tx.transaction_id}
                            sx={{
                              cursor: 'pointer',
                              ...(isNew ? newRowHighlightSx : {})
                            }}
                            onClick={() => setSelectedTx(tx)}
                          >
                            <TableCell sx={{ width: 150 }}>{formatTime(tx.block_created_at)}</TableCell>
                            <TableCell>#{tx.block_number}</TableCell>
                            <TableCell sx={{ width: 160, fontFamily: 'monospace' }}>{shortHash(tx.transaction_id)}</TableCell>
                            {isKuls && <TableCell sx={{ minWidth: 120 }}>{sourceOrgOf(tx) || '-'} → {targetOrgOf(tx) || '-'}</TableCell>}
                            <TableCell sx={{ width: 180 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Chip size="small" color={categoryColor(tx)} label={liveRecordType(tx)} />
                                {isNew && <Chip size="small" color="success" label={t("방금 반영")} />}
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ width: 220, fontFamily: 'monospace', wordBreak: 'break-word' }}>{liveBusinessId(tx)}</TableCell>
                            <TableCell sx={{ width: 150 }}>{liveOperation(tx)}</TableCell>
                            <TableCell>{formatBytes(livePayloadSizeBytes(tx))}</TableCell>
                            <TableCell sx={{ width: 140 }}>
                              <Stack spacing={0.5} alignItems="flex-start">
                                <LedgerStatusChip tx={tx} />
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {tab === 2 && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField
                    size="small"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={t("{0} block, txId, businessId, operation 검색", [partnerLabel])}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                    sx={{ minWidth: 360, flexGrow: 1 }}
                  />
                  {kulsFilterBar}
                  <Chip label={t("{0}개 블록", [visibleBlockRows.length])} color="primary" variant="outlined" />
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" sx={{ minWidth: 1020 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell><strong>{t("블록")}</strong></TableCell>
                        <TableCell><strong>{t("생성시각")}</strong></TableCell>
                        <TableCell><strong>{t("블록 내 Tx")}</strong></TableCell>
                        <TableCell><strong>{partnerLabel}{" "}{t("관련 Tx")}</strong></TableCell>
                        {isKuls && <TableCell><strong>{t("포함 출처")}</strong></TableCell>}
                        <TableCell><strong>{t("대표 업무 ID")}</strong></TableCell>
                        <TableCell><strong>{t("대표 작업")}</strong></TableCell>
                        <TableCell><strong>{t("대표 Tx ID")}</strong></TableCell>
                        <TableCell><strong>{t("Block Hash")}</strong></TableCell>
                        <TableCell><strong>{t("상태")}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleBlockRows.map(({ blockNumber, partnerRows, allRows, representative }) => {
                        const isNewBlock = newBlockNumbers.has(blockNumber);
                        const totalTxCount = representative.transaction_count || allRows.length;
                        const includedSources = Array.from(new Set(partnerRows.map(sourceOrgOf).filter(Boolean))).join(', ') || '-';
                        return (
                          <TableRow
                            hover
                            key={blockNumber}
                            sx={{
                              cursor: 'pointer',
                              ...(isNewBlock ? newRowHighlightSx : {})
                            }}
                            onClick={() => setSelectedBlockNumber(blockNumber)}
                          >
                            <TableCell sx={{ fontWeight: 700 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <span>#{blockNumber}</span>
                                {isNewBlock && <Chip size="small" color="success" label={t("방금 반영")} />}
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ width: 150 }}>{formatTime(representative.block_created_at)}</TableCell>
                            <TableCell>{totalTxCount}</TableCell>
                            <TableCell>{partnerRows.length}</TableCell>
                            {isKuls && <TableCell>{includedSources}</TableCell>}
                            <TableCell sx={{ width: 220, fontFamily: 'monospace', wordBreak: 'break-word' }}>{liveBusinessId(representative)}</TableCell>
                            <TableCell sx={{ width: 150 }}>{liveOperation(representative)}</TableCell>
                            <TableCell sx={{ width: 160, fontFamily: 'monospace' }}>{shortHash(representative.transaction_id)}</TableCell>
                            <TableCell sx={{ width: 150, fontFamily: 'monospace' }}>{shortHash(representative.block_hash || undefined)}</TableCell>
                            <TableCell sx={{ width: 140 }}>
                              <Stack spacing={0.5} alignItems="flex-start">
                                <LedgerStatusChip tx={representative} />
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {tab === 3 && (
            <Box>
              <Alert severity="warning" sx={{ mb: 3 }}>{t("This v1 guide covers {0}. It is based on the deployed API Gateway, chaincode v1.1 and WebApp index results. The final production contract may change after partner test feedback and review of actual production payloads.", [isKuls ? t("KULS 관점 통합 조회/검증") : t("{0} 연동 테스트", [partnerLabel])])}</Alert>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{t("1. 이 화면의 핵심")}</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <DetailField label={t("{0}가 하는 일", [partnerLabel])} value={ui(partnerPurpose)} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DetailField
                          label={t("Gateway가 하는 일")}
                          value={t("인증, 최소 schema 검증, payload 저장, canonical hash 계산, 공통 record 매핑, chaincode invoke를 처리합니다.")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DetailField
                          label={t("WebApp에 보이는 결과")}
                          value={t("commit 후 원장 상태(COMMITTED), 색인 상태(INDEXED), 무결성 상태(VERIFIED), 업무 상태(SUBMITTED)를 분리해서 조회합니다.")}
                        />
                      </Grid>
                    </Grid>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {isKuls ? t("KULS 화면은 eINS/KAIST가 기록한 결과를 targetOrg=KULS 관점으로 모아 봅니다. 현재 확보된 사실만으로 KULS 직접 invoke 구조를 확정하지 않습니다.") : t("협력사는 `chaincode`, `function`, `args[]`, `recordJSON`을 직접 만들지 않습니다. 업무 payload를 보내면 인제대 API Gateway가 `RecordIntegrationResult(recordJSON)`로 변환합니다.")}
                    </Alert>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{t("2. 기존 명세에서 v1 연동 구조로 바뀐 흐름")}</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell><strong>{t("단계")}</strong></TableCell>
                            <TableCell><strong>{t("무엇이 바뀌었는지")}</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {transitionRows.map(([step, description]) => (
                            <TableRow key={step}>
                              <TableCell sx={{ minWidth: 190, fontWeight: 700 }}>{ui(step)}</TableCell>
                              <TableCell>{ui(description)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Alert severity="success" sx={{ mt: 2 }}>{t("정리하면, 협력사는 기존 업무 payload를 보내고 인제대 Gateway가 공통 체인코드 명세 필드를 채운 뒤 invoke합니다. 즉, 협력사 업무 데이터와 블록체인 원장 기록 사이의 변환 책임은 Gateway가 맡습니다.")}</Alert>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{t("3. 실제 호출 정보")}</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>{isKuls ? t("KULS 화면 기준") : t("호출 방식 / 경로")}</TableCell>
                            <TableCell sx={{ fontFamily: isKuls ? 'inherit' : 'monospace' }}>
                              {isKuls ? t("eINS/KAIST 기록 중 KULS 관련 record를 조회합니다.") : t("POST {0}", [ui(endpointCandidate)])}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{t("Content-Type")}</TableCell>
                            <TableCell sx={{ fontFamily: isKuls ? 'inherit' : 'monospace' }}>
                              {isKuls ? t("KULS 화면은 WebApp 내부 조회 API/SSE를 사용합니다.") : t("application/json")}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{t("X-API-Key")}</TableCell>
                            <TableCell>{isKuls ? t("KULS 화면 조회용 계정 권한을 사용합니다. 협력사 invoke key와 분리됩니다.") : t("{0} key는 별도 전달합니다.", [partnerLabel])}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{t("인증 기준")}</TableCell>
                            <TableCell>{isKuls ? t("KULS 계정은 KULS 관련 record를 조회합니다. eINS/KAIST invoke endpoint 인증과는 별도입니다.") : t("{0} 전용 endpoint와 API key 조직이 일치해야 합니다.", [partnerLabel])}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{t("요청 본문 제한 / 저장 방식")}</TableCell>
                            <TableCell>
                              <Stack spacing={0.75}>
                                <Typography variant="body2">{t("현재 API Gateway의 JSON 요청 본문 제한은 8MB입니다. 이 값은 Gateway가 한 번에 수신할 수 있는 HTTP request body 제한이며, 블록체인 원장 저장 한도가 아닙니다.")}</Typography>
                                <Typography variant="body2">{t("v1 협력사 연동은 hash-ref on-chain 구조입니다. 원문 payload는 PostgreSQL에 저장하고, 블록체인에는 payloadHash, payloadRef, payloadSizeBytes, payloadStorageType과 공통 record 값을 기록합니다.")}</Typography>
                                <Typography variant="body2" color="text.secondary">{t("실제 운영 payload 크기 실측 후 8MB 제한 유지/상향, 대용량 payload 추적, 분할 전송 정책은 후속으로 조정할 수 있습니다.")}</Typography>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{t("4. 최소 검증")}</Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>{t("Zod는 업무 payload 전체를 엄격히 심사하지 않고, chaincode 기록과 WebApp 추적에 꼭 필요한 최소값만 차단 조건으로 봅니다. 그 외 추가 필드는 원문 payload에 보존됩니다.")}</Alert>
                    <Stack spacing={1.5}>
                      <DetailField label={t("필수값")} value={requiredFields.join(', ')} mono />
                      <DetailField label={t("기대값이지만 차단하지 않는 값")} value={expectedFields.map(ui).join(', ')} mono />
                      <DetailField label={t("metadata 정책")} value={isKuls ? t("KULS 화면은 Gateway가 이미 매핑한 공통 record와 payload projection을 조회합니다. KULS가 metadata를 별도 제출하는 구조가 아닙니다.") : isKaist ? t("KAIST v1 endpoint는 metadata를 필수로 요구하지 않습니다.") : t("metadata는 선택값입니다. 있으면 수용하지만 기준값은 scenario.scenario_id와 scenario.scen_type입니다.")} />
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={5}>
                  <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {isKuls ? t("5. KULS 표시 기준 예시") : t("5. 요청 body 예시")}
                    </Typography>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {isKuls ? t("예시는 KULS 화면에서 합쳐 보는 record 성격을 설명하기 위한 표시 기준입니다.") : t("예시는 현재 확보된 {0} 자료 기준입니다. 실제 운영 payload가 오면 optional field와 상세 rows는 더 늘어날 수 있습니다.", [partnerLabel])}
                    </Alert>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', maxHeight: 520, overflow: 'auto' }}>
                      <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(requestExample, null, 2)}</pre>
                    </Paper>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={7}>
                  <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{t("6. Gateway 처리 흐름")}</Typography>
                    <Grid container spacing={1.5}>
                      {[
                        ...gatewayFlowRows
                      ].map(([step, title, description]) => (
                        <Grid item xs={12} sm={6} key={step}>
                          <Paper variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: '#fff' }}>
                            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
                              <Chip label={ui(step)} size="small" color="primary" />
                              <Typography variant="subtitle2" fontWeight="bold">{ui(title)}</Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">{ui(description)}</Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{t("7. Payload → Common Record 매핑")}</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell><strong>{t("공통 필드")}</strong></TableCell>
                            <TableCell><strong>{t("값을 만드는 기준")}</strong></TableCell>
                            <TableCell><strong>{partnerLabel}{" "}{t("예시")}</strong></TableCell>
                            <TableCell><strong>{t("생성 주체")}</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {mappingRows.map(([field, source, example, createdBy]) => (
                            <TableRow key={field}>
                              <TableCell sx={{ fontFamily: 'monospace', minWidth: 210 }}>{field}</TableCell>
                              <TableCell>{ui(source)}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{typeof example === 'string' && example.includes(' 또는 ') ? ui(example) : example}</TableCell>
                              <TableCell>{ui(createdBy)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{t("8. 성공 응답")}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {isKuls ? t("KULS 화면은 invoke 응답을 직접 받는 화면이 아니라, commit/index가 끝난 기록을 조회하는 화면입니다.") : t("신규 payload는 정상 commit 시 HTTP 201로 응답합니다. 이미 같은 idempotency key로 commit된 payload는 HTTP 200과 `duplicate=true`로 반환됩니다.")}
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', maxHeight: 360, overflow: 'auto' }}>
                      <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(successResponseExample, null, 2)}</pre>
                    </Paper>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{t("9. 실패 응답")}</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell><strong>{t("상황")}</strong></TableCell>
                            <TableCell><strong>{t("HTTP / Code")}</strong></TableCell>
                            <TableCell><strong>{t("의미")}</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>{t("필수값 누락")}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{t("400 / BAD_REQUEST")}</TableCell>
                            <TableCell>{requiredFields.join(', ')}{" "}{t("중 누락 또는 형식 오류입니다.")}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{t("API key 없음/오류")}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{t("401 / UNAUTHORIZED")}</TableCell>
                            <TableCell>{t("인증 key가 없거나 유효하지 않습니다.")}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{isKuls ? t("조회 권한 불일치") : t("endpoint-org 불일치")}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{t("403 / FORBIDDEN")}</TableCell>
                            <TableCell>{isKuls ? t("KULS 관련 record 조회 권한이 없는 계정으로 접근한 경우입니다.") : t("{0} endpoint를 다른 조직 key로 호출한 경우입니다.", [partnerLabel])}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{t("body 과대")}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{t("413 후보 / Payload limit")}</TableCell>
                            <TableCell>{t("현재 Gateway HTTP JSON body 8MB 제한을 초과하면 Gateway 진입 단계에서 거절될 수 있습니다. 원장에는 원문 전체가 아니라 hash/ref 증거값만 기록되므로, 이는 블록체인 원장 저장 한도와는 별도입니다.")}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>{t("저장 또는 invoke 실패")}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{t("500 / INTERNAL_ERROR")}</TableCell>
                            <TableCell>{t("원문 저장 후 Fabric submit 또는 commit 처리 중 실패한 경우입니다. 응답의 recordId/correlationId를 인제대에 전달합니다.")}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{t("10. WebApp 표시 연결")}</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell><strong>{t("값")}</strong></TableCell>
                            <TableCell><strong>{t("의미")}</strong></TableCell>
                            <TableCell><strong>{t("표시 위치")}</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayRows.map(([value, meaning, display]) => (
                            <TableRow key={value}>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{value}</TableCell>
                              <TableCell>{ui(meaning)}</TableCell>
                              <TableCell>{ui(display)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info">{t("현재 화면은 Block #")}{PROTOTYPE_FROM_BLOCK}{" "}{t("이후 기록만 표시합니다. 신규 v1 기록은 hash-ref 구조이고, 기존 RecordEvent 기록은 호환성 확인용으로 분리해서 봅니다.")}</Alert>
                </Grid>
              </Grid>
            </Box>
          )}
        </>
      )}

      <BlockDetailDialog
        block={selectedBlock}
        partnerLabel={partnerLabel}
        proofs={proofs}
        isPartnerTransaction={isPartnerTransaction}
        onClose={() => setSelectedBlockNumber(null)}
        onSelectTx={(tx) => {
          setSelectedBlockNumber(null);
          setSelectedTx(tx);
        }}
      />
      <LiveTxDetailDialog tx={selectedTx} proof={selectedTx ? proofs[selectedTx.transaction_id] : undefined} onClose={() => setSelectedTx(null)} />
    </Box>
  );
}

export default PartnerIntegrationPrototype;
